import { FC } from 'react'
import BarChart from '../components/Analytics/Charts/BarChart'
import BubbleChart from '../components/Analytics/Charts/BubbleChart'
import LineChart from '../components/Analytics/Charts/LineChart'
import StockedBarChart from '../components/Analytics/Charts/StockedBarChart'
import { SOCIAL_NETWORK } from './post'

export enum AVAILABLE_FACEBOOK_METRICS {
  page_fans = 'page_fans',
  page_impressions = 'page_impressions',
  page_impressions_paid = 'page_impressions_paid',
  page_impressions_organic = 'page_impressions_organic',
  page_impressions_unique = 'page_impressions_unique',
  page_impressions_paid_unique = 'page_impressions_paid_unique',
  page_impressions_organic_unique = 'page_impressions_organic_unique',
  page_engaged_users = 'page_engaged_users',
  page_post_engagements = 'page_post_engagements',
  page_views_total = 'page_views_total',
  page_fans_online = 'page_fans_online',
  page_fan_adds = 'page_fan_adds',
  page_fan_removes = 'page_fan_removes'
}

export enum AVAILABLE_INSTAGRAM_METRICS {
  follower_count = 'follower_count',
  impressions = 'impressions',
  profile_views = 'profile_views',
  reach = 'reach',
  story_reach = 'story_reach',
  story_engagement = 'story_engagement',
  page_fan_adds = 'page_fan_adds'
}

export enum AVAILABLE_LINKEDIN_METRICS {
  unique_impressions_count = 'uniqueImpressionsCount',
  share_count = 'shareCount',
  engagement = 'engagement',
  engagementRate = 'engagementRate',
  click_count = 'clickCount',
  like_count = 'likeCount',
  impression_count = 'impressionCount',
  comment_count = 'commentCount',
  total_page_views = 'allPageViews',
  paid_follower_count = 'paidFollowerCount',
  organic_follower_count = 'organicFollowerCount',
  organic_follower_count_add = 'organicFollowerCountAdd',
  paid_follower_count_add = 'paidFollowerCountAdd',
  //!!!CUSTOM METRIC!!!
  //This metric is not provided from linkedin api
  //The metric is general count from share count + click count + like count + comment count
  total_engagement = 'totalEngagement'
  //The metric is general count from share count + click count + like count + comment count
  //This metric is not provided from linkedin api
  //!!!CUSTOM METRIC!!!
}

export const totalEngagement = [
  AVAILABLE_LINKEDIN_METRICS.share_count,
  AVAILABLE_LINKEDIN_METRICS.click_count,
  AVAILABLE_LINKEDIN_METRICS.like_count,
  AVAILABLE_LINKEDIN_METRICS.comment_count
]

export enum AVAILABLE_TWITTER_METRICS {
  retweet_count = 'retweet_count',
  reply_count = 'reply_count',
  like_count = 'like_count',
  quote_count = 'quote_count',
  followers_count = 'followers_count'
}

export enum AVAILABLE_GRAPHIC {
  TOTAL_FOLLOWERS = 'total-followers',
  TOTAL_NEW_FOLLOWERS = 'total-new-followers',
  TOTAL_IMPRESSIONS = 'total-impressions',
  TOTAL_REACH = 'total-reach',
  TOTAL_ENGAGMENT = 'total-engagement',
  PROFILE_IMPRESSIONS = 'profile-impressions',
  STORIES_ENGAGEMENT = 'story-engagement',
  STORIES_REACH = 'story-reach',
  ENGAGEMENT_RATE = 'engagement-rate',
  TWEET_LIKES = 'tweet-likes',
  RETWEET = 'retweet',
  NEW_FOLLOWERS = 'new-followers',
  TOTAL_FOLLOWERS_CARD = 'total-followers-card',
  TOTAL_PAGE_VIEWS = 'total-page-views'
}

export const GRAPHIC_DISPLAY_NAME: { [key in AVAILABLE_GRAPHIC]?: string } = {
  [AVAILABLE_GRAPHIC.ENGAGEMENT_RATE]: 'engagement rate %',
}

export const CHART_COMPONENTS = {
  BAR_CHART: BarChart,
  BUBBLE_CHART: BubbleChart,
  LINE_CHART: LineChart,
  STOCKED_BAR_CHART: StockedBarChart
}

export type TGraphicSettings = {
  cumulative: boolean
  organic: boolean
}

export enum TYPE_METRIC {
  TYPE_ORGANIC = 'organic',
  TYPE_PAID = 'paid',
  TYPE_POSITIVE = 'positive',
  TYPE_NEGATIVE = 'negative',
  TYPE_LABELES = 'labels'
}

export enum TYPE_GRAPHIC {
  TYPE_COMULATIVE = 'cumulative',
  TYPE_NON_COMULATIVE = 'non_cumulative'
}

export type TMetric = {
  type: TYPE_METRIC
  metric:
  | AVAILABLE_FACEBOOK_METRICS
  | AVAILABLE_INSTAGRAM_METRICS
  | AVAILABLE_LINKEDIN_METRICS
  | AVAILABLE_TWITTER_METRICS
}

export type TGraphic = {
  name: AVAILABLE_GRAPHIC
  settings: TGraphicSettings
  chartComponent: FC<ChartProps>
  metrics: TMetric[]
}

export interface ChartProps {
  social: SOCIAL_NETWORK
  name: AVAILABLE_GRAPHIC
  chartData: any
}

export type TMetricValue = {
  date: string
  extended_value: number | null
  name:
  | AVAILABLE_FACEBOOK_METRICS
  | AVAILABLE_INSTAGRAM_METRICS
  | AVAILABLE_LINKEDIN_METRICS
  | AVAILABLE_TWITTER_METRICS
  value: number | null
}

type TSocialGraphics = {
  [key in SOCIAL_NETWORK]: {
    [key in AVAILABLE_GRAPHIC]?: TGraphic
  }
}

export const SOCIAL_GRAPHICS: TSocialGraphics = {
  [SOCIAL_NETWORK.FACEBOOK_SOCIAL]: {
    [AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_NEW_FOLLOWERS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_NEGATIVE,
          metric: AVAILABLE_FACEBOOK_METRICS.page_fan_removes
        },
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_FACEBOOK_METRICS.page_fan_adds
        }
      ],
      chartComponent: CHART_COMPONENTS.STOCKED_BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS,
      settings: {
        cumulative: true,
        organic: true
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_ORGANIC,
          metric: AVAILABLE_FACEBOOK_METRICS.page_impressions_organic
        },
        {
          type: TYPE_METRIC.TYPE_PAID,
          metric: AVAILABLE_FACEBOOK_METRICS.page_impressions_paid
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_REACH]: {
      name: AVAILABLE_GRAPHIC.TOTAL_REACH,
      settings: {
        cumulative: true,
        organic: true
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_ORGANIC,
          metric: AVAILABLE_FACEBOOK_METRICS.page_impressions_organic_unique
        },
        {
          type: TYPE_METRIC.TYPE_PAID,
          metric: AVAILABLE_FACEBOOK_METRICS.page_impressions_paid_unique
        }
      ],
      chartComponent: CHART_COMPONENTS.BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT]: {
      name: AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_FACEBOOK_METRICS.page_post_engagements
        }
      ],
      chartComponent: CHART_COMPONENTS.BUBBLE_CHART
    },
    [AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS]: {
      name: AVAILABLE_GRAPHIC.PROFILE_IMPRESSIONS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_FACEBOOK_METRICS.page_impressions
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    }
  },
  [SOCIAL_NETWORK.INSTAGRAM_SOCIAL]: {
    [AVAILABLE_GRAPHIC.NEW_FOLLOWERS]: {
      name: AVAILABLE_GRAPHIC.NEW_FOLLOWERS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        // {
        //   type: TYPE_METRIC.TYPE_NEGATIVE,
        //   metric: AVAILABLE_FACEBOOK_METRICS.page_fan_removes
        // },
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_FACEBOOK_METRICS.page_fan_adds
        }
      ],
      chartComponent: CHART_COMPONENTS.STOCKED_BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_INSTAGRAM_METRICS.impressions
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_REACH]: {
      name: AVAILABLE_GRAPHIC.TOTAL_REACH,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_INSTAGRAM_METRICS.reach
        }
      ],
      chartComponent: CHART_COMPONENTS.BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT]: {
      name: AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_INSTAGRAM_METRICS.profile_views
        }
      ],
      chartComponent: CHART_COMPONENTS.BUBBLE_CHART
    },
    [AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT]: {
      name: AVAILABLE_GRAPHIC.STORIES_ENGAGEMENT,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_INSTAGRAM_METRICS.story_engagement
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    },
    [AVAILABLE_GRAPHIC.STORIES_REACH]: {
      name: AVAILABLE_GRAPHIC.STORIES_REACH,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_INSTAGRAM_METRICS.story_reach
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    }
  },
  [SOCIAL_NETWORK.LINKEDIN_SOCIAL]: {
    [AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS,
      settings: {
        cumulative: true,
        organic: true
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_ORGANIC,
          metric: AVAILABLE_LINKEDIN_METRICS.organic_follower_count_add
        },
        {
          type: TYPE_METRIC.TYPE_PAID,
          metric: AVAILABLE_LINKEDIN_METRICS.paid_follower_count_add
        }
      ],
      chartComponent: CHART_COMPONENTS.STOCKED_BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_PAGE_VIEWS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_LINKEDIN_METRICS.total_page_views
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT]: {
      name: AVAILABLE_GRAPHIC.TOTAL_ENGAGMENT,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_LINKEDIN_METRICS.total_engagement
        }
      ],
      chartComponent: CHART_COMPONENTS.BUBBLE_CHART
    },
    [AVAILABLE_GRAPHIC.ENGAGEMENT_RATE]: {
      name: AVAILABLE_GRAPHIC.ENGAGEMENT_RATE,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_LINKEDIN_METRICS.engagementRate
        }
      ],
      chartComponent: CHART_COMPONENTS.BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_IMPRESSIONS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_LINKEDIN_METRICS.impression_count
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    }
  },
  [SOCIAL_NETWORK.TWITTER_SOCIAL]: {
    [AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS]: {
      name: AVAILABLE_GRAPHIC.TOTAL_FOLLOWERS,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_TWITTER_METRICS.followers_count
        }
      ],
      chartComponent: CHART_COMPONENTS.STOCKED_BAR_CHART
    },
    [AVAILABLE_GRAPHIC.TWEET_LIKES]: {
      name: AVAILABLE_GRAPHIC.TWEET_LIKES,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_TWITTER_METRICS.like_count
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    },
    [AVAILABLE_GRAPHIC.RETWEET]: {
      name: AVAILABLE_GRAPHIC.RETWEET,
      settings: {
        cumulative: true,
        organic: false
      },
      metrics: [
        {
          type: TYPE_METRIC.TYPE_POSITIVE,
          metric: AVAILABLE_TWITTER_METRICS.retweet_count
        }
      ],
      chartComponent: CHART_COMPONENTS.LINE_CHART
    }
  }
}

export const INTERVAL = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly'
}

export enum TInterval {
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
  quarterly = 'quarterly'
}

export enum TTopPerformance {
  reach = 'reach',
  engagement = 'engagement'
}

export interface ITopPerformance {
  comments: number
  likes: number
  scheduled_at: string
  timezone: string
  postSocial: {
    attachments: Array<any>,
    message: string
  }
  reach: number
  shares: number
}